import React, { useState } from 'react';
import { 
  Edit, Trash2, BookOpen
} from 'lucide-react';
import EXAM_API from '../../api/services/examApi';
import SettingsHeader from '../../ui/SettingsHeader';
import Loader from '../../ui/Loader';
import ConfirmationModal from '../../ui/ConfirmationModal';
import toast from 'react-hot-toast';

const ExamDetails = ({ exam, onBack, onEdit, onDelete }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteExam = async () => {
    try {
      await EXAM_API.deleteExam(exam._id || exam.id);
      toast.success('Exam deleted successfully');
      setShowDeleteModal(false);
      onDelete(exam._id || exam.id);
    } catch (error) { 
      toast.error('Failed to delete exam'); 
    }
  };

  const getClassInfo = () => {
    if (exam.classes && exam.classes.length > 0) {
      return exam.classes.map(cls => cls.unitId?.unitName || cls.unitName).filter(Boolean).join(', ');
    }
    return 'All Classes';
  };

  const allSubjects = exam.classes?.flatMap(cls => cls.subjects || []) || [];
  const totalPossibleMarks = allSubjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0) || 0;

  if (!exam) return <Loader />;

  return (
    <div className="mx-auto max-w-5xl py-6 space-y-6">
      <SettingsHeader
        title={exam.examName}
        description={`${getClassInfo()} • ${exam.academicYear}`}
        showBack={true}
        onBack={onBack}
        icon={BookOpen}
        buttons={[
          { label: "Edit", icon: Edit, onClick: () => onEdit(exam), variant: "secondary" },
          { label: "Delete", icon: Trash2, onClick: () => setShowDeleteModal(true), variant: "destructive" },
        ]}
      />

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Exam Information</h3>
          <span className={`px-3 py-1 rounded-md text-xs font-medium border ${exam.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
            {exam.isActive ? 'Published' : 'Draft'}
          </span>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">Exam Type</label>
              <p className="text-sm font-semibold text-gray-900">{exam.examType}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">Academic Year</label>
              <p className="text-sm font-semibold text-gray-900">{exam.academicYear}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">Total Marks</label>
              <p className="text-sm font-semibold text-gray-900">{totalPossibleMarks}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">Subjects</label>
              <p className="text-sm font-semibold text-gray-900">{allSubjects.length}</p>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Classes</label>
            <div className="flex flex-wrap gap-2">
              {exam.classes?.map((c, i) => (
                <span key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium text-gray-700">
                  {c.unitId?.unitName || c.unitName}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteExam}
          title="Delete Examination"
          message={`Are you sure you want to delete ${exam.examName}? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default ExamDetails;
